import { Box, Typography, Stack, Drawer, Menu, MenuItem, IconButton, InputBase, Button } from '@mui/material';

import { useEffect, useState, useRef, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useSelector } from '../../redux/store';
import Iconify from '../Iconify';
import useResponsive from '../../hooks/useResponsive';
import Image from '../Image';
import useAuth from '../../hooks/useAuth';
import GameContainer from '../../views/game/GameContainer';
import { calculateFontSize } from '../../utils/formatString';
import GamesByCateg from '../../views/game/GamesByCateg';
import useLocales from '../../hooks/useLocales';

const navigationIcon = [
  { label: 'HOME', icon: 'mdi:home-outline', path: '/' },
  { label: 'SLOT', icon: 'mdi:slot-machine', path: '/game/slots' },
  { label: 'CRASH GAME', icon: 'mdi:rocket-launch-outline', path: '/game/crash-game' },
  { label: 'TABLE GAME', icon: 'mdi:table-furniture', path: '/game/table-game' },
  { label: 'FISHING', icon: 'mdi:fish', path: '/game/fishing' },
];

const categoryIcons = {
  casino: 'mdi:cards-playing-outline',
  slots: 'mdi:slot-machine',
  fishing: 'mdi:fish',
  'e-sports': 'mdi:controller-classic-outline',
  table: 'mdi:poker-chip',
  'instant-game': 'mdi:flash-outline',
};

const ITEM_MIN_WIDTH = 140;
const MORE_BUTTON_WIDTH = 100;
const PROMO_WIDTH = 130;

export const useResponsiveNav = (items) => {
  const containerRef = useRef(null);
  const frameRef = useRef(null);

  const [visibleItems, setVisibleItems] = useState(items);
  const [overflowItems, setOverflowItems] = useState([]);

  useEffect(() => {
    const calculate = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;

      const availableWidth = containerWidth - PROMO_WIDTH;

      let maxItems = Math.floor(availableWidth / ITEM_MIN_WIDTH);

      if (maxItems < items.length) {
        maxItems = Math.floor((availableWidth - MORE_BUTTON_WIDTH) / ITEM_MIN_WIDTH);
      }

      if (maxItems >= items.length) {
        maxItems = items.length;
      } else {
        maxItems = Math.max(0, maxItems);
      }

      maxItems = Math.max(0, maxItems);

      const newVisible = maxItems >= items.length ? items : items.slice(0, maxItems);

      const newOverflow = maxItems >= items.length ? [] : items.slice(maxItems);

      setVisibleItems((prev) => (JSON.stringify(prev) === JSON.stringify(newVisible) ? prev : newVisible));

      setOverflowItems((prev) => (JSON.stringify(prev) === JSON.stringify(newOverflow) ? prev : newOverflow));
    };

    const handleResize = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(calculate);
    };

    const observer = new ResizeObserver(handleResize);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    calculate();

    return () => {
      cancelAnimationFrame(frameRef.current);
      observer.disconnect();
    };
  }, [items]);

  return { containerRef, visibleItems, overflowItems };
};

const CustomDrawer = ({ open, onClose, items = [], onItemClick }) => {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          backgroundColor: '#f5f5f5',
        },
      }}
    >
      <Stack spacing={1} sx={{ px: 2 }}>
        {items.map((item) => (
          <Stack
            key={item.label}
            direction="row"
            spacing={2}
            alignItems="center"
            onClick={() => {
              onItemClick?.(item);
            }}
            sx={{
              py: 1.5,
              px: 1,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            <Iconify icon={item.icon} sx={{ width: 24, height: 24, color: '#000000' }} />
            <Typography fontWeight={500} color={'#000000'}>
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Drawer>
  );
};

const NavigationHeader = ({ active = 'HOME', onItemClick }) => {
  const { translate } = useLocales();

  const [openDrawer, setOpenDrawer] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  const [selectedFilter, setSelectedFilter] = useState('');

  const { category } = useParams();

  const location = useLocation();

  const [currentGamesandCategs, setCurrentGamesandCategs] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');

  const [selectedProvider, setSelectedProvider] = useState(null);

  const [customArrangements, setCustomArrangements] = useState([]);

  const [secondLevelTabs, setSecondLevelTabs] = useState([]);

  const [secondLevelCategId, setSecondLevelCategId] = useState({ categSlug: '', companyId: '' });

  const { isAuthenticated } = useAuth();

  const [isFixed, setIsFixed] = useState(false);

  const isDesktop = useResponsive('up', 'lg');

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);

  const [hoverParams, setHoverParams] = useState(null);

  const [selectedGameParams, setSelectedGameParams] = useState(null);

  const [hoverData, setHoverData] = useState(null);

  const isHomeActive = location.pathname === '/';

  const isPromotionActive = location.pathname === '/promotion/tab';

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const scrollRef = useRef();

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -150 : 150,
        behavior: 'smooth',
      });
    }
  };

  const { gameCategsAndCompanies } = useSelector((x) => x.lookup);

  const NAV_ITEMS =
    gameCategsAndCompanies?.map((item) => {
      const key = item.slug.toLowerCase();
      const path = `/arrangement/${item.slug}/${item.companies[0].id}`;

      return {
        label: item.name.toUpperCase(),
        icon: categoryIcons[key] || 'mdi:gamepad-variant-outline', // fallback
        path,
        active: location.pathname === path,
        data: item,
      };
    }) || [];

  const { containerRef, visibleItems, overflowItems } = useResponsiveNav(NAV_ITEMS);

  useEffect(() => {
    const handleScroll = () => {
      const triggerPoint = 120; // adjust this

      if (window.scrollY > triggerPoint) {
        setIsFixed(true);
      } else {
        setIsFixed(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // dispatch(openLoadingScreen());

    // conObj
    //   .post('games/game_categories.json')
    //   .then((response) => {
    // const arr = response?.data?.data?.data || [];
    const arr = gameCategsAndCompanies || [];
    setCustomArrangements(arr);

    if (arr.length === 0) return;

    // map URL category or default first one
    const initialCategory = category || arr[0].slug;
    setSelectedCategory(initialCategory);

    const selected = arr.find((c) => c.slug === initialCategory);

    if (!selected) return;

    // if (selected.type === 'company_with_categories') {
    //   setSecondLevelTabs(selected.companies || []);

    //   setSecondLevelCategId({
    //     categSlug: selected.companies?.[0]?.category_slug || '',
    //     companyId: selected.companies?.[0]?.id || '',
    //   });

    //   setCurrentGamesandCategs([]);
    // } else {
    if (selected.type === 'company_with_categories') {
      const companies = selected.companies || [];

      setSecondLevelTabs(companies);

      const first = companies[0];

      if (first) {
        setSelectedProvider(first);

        setSecondLevelCategId({
          categSlug: first.category_slug || '',
          companyId: first.id || '',
        });
      }

      setCurrentGamesandCategs([]);
    } else {
      setSecondLevelTabs([]);
      setSelectedProvider(null);
      setSecondLevelCategId({ categSlug: '', companyId: '' });
      setCurrentGamesandCategs(selected.companies || []);
    }
    // }
    // })
    // .finally(() => dispatch(closeLoadingScreen()));
  }, [category, gameCategsAndCompanies]);

  useEffect(() => {
    if (currentGamesandCategs.length > 0) {
      const firstGame = currentGamesandCategs[0];

      setSelectedGameParams({
        categSlug: firstGame.category_slug,
        companyId: firstGame.id,
        gameName: firstGame.name,
      });
    }
  }, [currentGamesandCategs]);

  useEffect(() => {
    setSearchValue('');
  }, [category]);

  return (
    <>
      {isDesktop && (
        <Box sx={{ position: 'relative' }} onMouseLeave={() => setHoverData(null)}>
          <Box
            ref={containerRef}
            sx={{
              background: 'linear-gradient(180deg, #ffe08d, #e09944)',
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              overflow: 'hidden',
            }}
          >
            {/* LEFT SIDE */}
            <Box
              sx={{
                overflow: 'hidden',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  onClick={() => navigate('/')}
                  sx={{
                    cursor: 'pointer',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Iconify
                    icon="mdi:home"
                    sx={{
                      width: 25,
                      height: 25,
                      color: isHomeActive ? '#fff' : '#000',
                      transition: 'color 0.2s',
                    }}
                  />

                  <Typography
                    fontWeight={700}
                    sx={{
                      color: isHomeActive ? '#fff' : '#000',
                      transition: 'color 0.2s',
                    }}
                  >
                    {translate('home')}
                  </Typography>
                </Stack>
                {visibleItems.map((item) => {
                  const isActive = category === item.data?.slug;

                  return (
                    <Stack
                      key={item.label}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      cursor={'pointer'}
                      onClick={() => {
                        navigate(item.path);
                      }}
                      onMouseEnter={() => {
                        setHoverData(item.data);
                      }}
                      sx={{
                        cursor: 'pointer',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      <Iconify
                        icon={item.icon}
                        sx={{
                          width: 25,
                          height: 25,
                          color: isActive ? '#fff' : '#000',
                        }}
                      />

                      <Typography
                        fontWeight={700}
                        sx={{
                          color: isActive ? '#fff' : '#000',
                        }}
                      >
                        {translate(item.label)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>

            {/* RIGHT SIDE */}
            <Box display="flex" alignItems="center" gap={1}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() => navigate('/promotion/tab')}
                sx={{
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <Iconify
                  icon="mdi:gift-outline"
                  sx={{
                    width: 25,
                    height: 25,
                    color: isPromotionActive ? '#fff' : '#000',
                    transition: 'color 0.2s',
                  }}
                />

                <Typography
                  sx={{
                    color: isPromotionActive ? '#fff' : '#000',
                    fontWeight: 600,
                    transition: 'color 0.2s',
                  }}
                >
                  {translate('promotion')}
                </Typography>
              </Stack>

              {overflowItems.length > 0 && (
                <Box
                  onClick={(e) => {
                    if (isDesktop) {
                      handleOpenMenu(e);
                    } else {
                      setOpenDrawer(true);
                    }
                  }}
                  sx={{
                    color: '#000000',
                    border: '1px solid #000',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                  }}
                >
                  MORE
                </Box>
              )}
            </Box>
          </Box>

          {hoverData && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                background: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(6px)',
                px: 4,
                zIndex: 999,
                opacity: hoverData ? 1 : 0,
                transform: hoverData ? 'translateY(0px)' : 'translateY(-20px)',
                transition: 'all 0.25s ease',
                pointerEvents: hoverData ? 'auto' : 'none',
              }}
            >
              <Stack direction="row" spacing={4} flexWrap="wrap" justifyContent="center" alignItems="center">
                {hoverData?.companies?.map((company) => (
                  <Box
                    key={company.id}
                    onClick={() => {
                      navigate(`/arrangement/${company.category_slug}/${company.id}`);
                    }}
                    sx={{
                      width: 190,
                      cursor: 'pointer',
                      p: 1,
                      position: 'relative',
                      transition: 'all 0.2s',

                      '&:hover': {
                        backgroundColor: '#BFBFBF',
                      },

                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '100%',
                        height: '0px',
                        backgroundColor: '#F1C16D',
                        transition: 'height 0.2s',
                      },

                      '&:hover::after': {
                        height: '10px',
                      },
                    }}
                  >
                    <Image
                      src={company.icon_url}
                      alt={company.name}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '1 / 1',
                        objectFit: 'contain',
                        mb: 1,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {isDesktop && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#fff',
                    color: '#000',
                    borderRadius: '4px',
                    boxShadow: 3,
                    minWidth: 160,
                  },
                },
              }}
            >
              {overflowItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    navigate(item.path);
                    onItemClick?.(item);
                    handleCloseMenu();
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon={item.icon} width={20} />
                    <Typography>{item.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          )}

          {!isDesktop && (
            <CustomDrawer
              open={openDrawer}
              onClose={() => setOpenDrawer(false)}
              items={overflowItems}
              onItemClick={(item) => {
                navigate(item.path);
                onItemClick?.(item);
                setOpenDrawer(false);
              }}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default NavigationHeader;
